import { useAuthStore } from '@/lib/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;

function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(response: Response, errorPrefix: string): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `${errorPrefix}: ${response.status}`);
  }
  return response.json();
}

export type InvestigationOrderStatus = 'requested' | 'accepted' | 'completed';

export interface InvestigationOrderReport {
  id: string;
  contentType?: string;
  fileName?: string;
  binaryRef?: string;
}

export interface InvestigationOrder {
  taskId: string;
  serviceRequestId: string;
  status: InvestigationOrderStatus;
  patientRef?: string;
  patientId?: string;
  patientName?: string;
  practitionerName?: string;
  investigations?: string;
  authoredOn?: string;
  encounterId?: string;
  hasReport: boolean;
  report: InvestigationOrderReport | null;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  seenBy?: string;
  seenAt?: string;
}

export interface CreateInvestigationOrderInput {
  patientId: string;
  patientName?: string;
  practitionerId: string;
  practitionerName?: string;
  encounterId: string;
  investigations: string[];
}

export const investigationOrdersApi = {
  create: async (
    data: CreateInvestigationOrderInput
  ): Promise<{ serviceRequest: unknown; task: unknown; alreadyExists?: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/api/investigation-orders`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return handleResponse(response, 'Failed to create investigation order');
  },

  list: async (
    params: {
      status?: 'requested' | 'accepted' | 'completed' | 'all';
      search?: string;
    } = {}
  ): Promise<InvestigationOrder[]> => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);

    const response = await fetch(`${API_BASE_URL}/api/investigation-orders?${query.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const data = await handleResponse<{ orders: InvestigationOrder[] }>(
      response,
      'Failed to fetch investigation orders'
    );
    return data.orders;
  },

  acknowledge: async (taskId: string, acknowledgedBy?: string): Promise<{ task: unknown }> => {
    const response = await fetch(
      `${API_BASE_URL}/api/investigation-orders/${encodeURIComponent(taskId)}/acknowledge`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ acknowledgedBy }),
      }
    );

    return handleResponse(response, 'Failed to acknowledge investigation order');
  },

  markSeen: async (taskId: string, seenBy?: string): Promise<{ task: unknown }> => {
    const response = await fetch(
      `${API_BASE_URL}/api/investigation-orders/${encodeURIComponent(taskId)}/seen`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ seenBy }),
      }
    );

    return handleResponse(response, 'Failed to mark investigation order as seen');
  },

  cancel: async (serviceRequestId: string): Promise<{ serviceRequest: unknown }> => {
    const response = await fetch(
      `${API_BASE_URL}/api/investigation-orders/${encodeURIComponent(serviceRequestId)}/cancel`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
      }
    );

    return handleResponse(response, 'Failed to cancel investigation order');
  },
};
